/**
 * @module components/AI/AIHeaderButton
 * @description Botón del asistente IA integrado en la barra de navegación superior.
 *
 * Alternativa a `AIFloatingButton` diseñada para integrarse en el `Header`
 * sin ocupar espacio flotante sobre el contenido. Abre un panel desplegable
 * con `ChatAssistant` y `AISuggestions`.
 *
 * @see AIFloatingButton para la variante flotante en esquina inferior derecha
 * @see Header donde este componente está montado
 */
import React, { useState } from 'react';
import { Sparkles, X, MessageSquare, Lightbulb } from 'lucide-react';
import { ChatAssistant } from './ChatAssistant';
import { AISuggestions } from './AISuggestions';
import { useAI } from '../../contexts/AIContext';

export function AIHeaderButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'chat' | 'suggestions'>('chat');
  const { isEnabled } = useAI();

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 ${
          isOpen
            ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
            : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'
        }`}
        aria-label="Asistente de IA"
        title="Asistente de IA"
      >
        <Sparkles className="w-5 h-5" />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Panel */}
          <div className="absolute top-full right-0 mt-2 w-[32rem] max-h-[calc(100vh-8rem)] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col z-50">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  <h3 className="font-semibold text-lg">Asistente de IA</h3>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setActiveTab('chat')}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeTab === 'chat'
                      ? 'bg-white text-purple-600 shadow-md'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  <MessageSquare className="w-4 h-4 inline mr-2" />
                  Chat
                </button>
                <button
                  onClick={() => setActiveTab('suggestions')}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeTab === 'suggestions'
                      ? 'bg-white text-blue-600 shadow-md'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  <Lightbulb className="w-4 h-4 inline mr-2" />
                  Sugerencias
                </button>
              </div>
            </div>

            {/* Contenido */}
            <div className="flex-1 overflow-y-auto">
              {!isEnabled ? (
                <div className="p-6">
                  <div className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border-2 border-purple-200 dark:border-purple-800 rounded-xl p-6 text-center">
                    <div className="inline-flex p-4 bg-purple-100 dark:bg-purple-900/30 rounded-full mb-4">
                      <Sparkles className="w-12 h-12 text-purple-600 dark:text-purple-400" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      ¡Configura tu Asistente de IA!
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-6">
                      Para usar el asistente necesitas configurar tu API key de Google Gemini.
                      <br />
                      <strong>Es gratis</strong> - 15 solicitudes por minuto sin costo.
                    </p>
                    <div className="space-y-3 mb-6 text-left bg-white dark:bg-gray-800 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          Ve a{' '}
                          <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-purple-600 dark:text-purple-400 underline hover:no-underline font-medium">
                            Google AI Studio
                          </a>
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                        <p className="text-sm text-gray-700 dark:text-gray-300">Crea una API key (gratis)</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                        <p className="text-sm text-gray-700 dark:text-gray-300">Pégala en Settings → Asistente IA</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        window.dispatchEvent(new CustomEvent('openSettings', { detail: { tab: 'ai' } }));
                      }}
                      className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl"
                    >
                      Ir a Configuración
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {activeTab === 'chat' ? (
                    <div className="h-full">
                      <ChatAssistant className="h-full border-0 rounded-none" />
                    </div>
                  ) : (
                    <div className="text-center py-12 px-4">
                      <div className="inline-flex p-4 bg-gray-100 dark:bg-gray-700/50 rounded-full mb-4">
                        <Lightbulb className="w-12 h-12 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Selecciona un control ISO para ver sugerencias contextuales.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
